import { zodResolver } from '@hookform/resolvers/zod';
import { Role, User, UserStatuses } from '@prisma/client';
import { SafeUser } from '@zond/api-interfaces';
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@zond/ui';
import React, { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { useSignUp, useUpdateUser, useUsers } from './user.queries';

interface UserFormProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  mode: 'create' | 'edit';
  initialData?: SafeUser;
}

export default function UserForm({ open, setOpen, mode, initialData }: UserFormProps) {
  const { t } = useTranslation('users');
  const { refetch } = useUsers();

  const userSchema = z.object({
    email: z.string().email(t('form.validation.invalidEmail')),
    phone: z.string().optional(),
    password: z.string().optional(),
    firstname: z.string(),
    lastname: z.string(),
    role: z.nativeEnum(Role),
    status: z.nativeEnum(UserStatuses),
  });

  type UserFormData = z.infer<typeof userSchema>;

  const defaultValues: UserFormData = useMemo(() => {
    return {
      email: '',
      phone: '',
      password: '',
      firstname: '',
      lastname: '',
      role: Role.USER,
      status: UserStatuses.Active,
    };
  }, []);

  const form = useForm<UserFormData>({
    resolver: zodResolver(userSchema),
    defaultValues,
    mode: 'onChange',
  });

  useEffect(() => {
    if (initialData) {
      form.reset({
        email: initialData.email ?? defaultValues.email,
        phone: defaultValues.phone,
        firstname: initialData.firstname ?? defaultValues.firstname,
        lastname: initialData.lastname ?? defaultValues.lastname,
        role: defaultValues.role,
      });
    } else {
      form.reset(defaultValues);
    }
  }, [initialData, form, defaultValues]);

  const onSuccess = () => {
    setOpen(false);
    form.reset();
    refetch();
  };

  const { mutate: signUp, isPending: isSignUpPending } = useSignUp(onSuccess);
  const { mutate: updateUser, isPending: isUpdatePending } = useUpdateUser(onSuccess);

  const onSubmit = (data: UserFormData) => {
    if (mode === 'create') {
      signUp(data);
    } else {
      if (initialData) {
        updateUser({
          id: initialData.id,
          ...data,
        });
      }
    }
  };

  const isPending = isSignUpPending || isUpdatePending;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{mode === 'create' ? t('form.title.create') : t('form.title.edit')}</DialogTitle>
          <DialogDescription>
            {mode === 'create' ? t('form.description.create') : t('form.description.edit')}
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('common:labels.email')}</FormLabel>
                      <FormControl>
                        <Input type="email" placeholder={t('form.placeholders.email')} {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="phone"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('fields.phone')}</FormLabel>
                      <FormControl>
                        <Input type="tel" placeholder={t('form.placeholders.phone')} {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {mode === 'create' ? t('form.fields.password') : t('form.fields.newPassword')}
                      </FormLabel>
                      <FormControl>
                        <Input
                          type="password"
                          placeholder={
                            mode === 'create'
                              ? t('form.placeholders.password')
                              : t('form.placeholders.newPassword')
                          }
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="firstname"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('fields.firstName')}</FormLabel>
                      <FormControl>
                        <Input placeholder={t('form.placeholders.firstName')} {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="lastname"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('fields.lastName')}</FormLabel>
                      <FormControl>
                        <Input placeholder={t('form.placeholders.lastName')} {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="role"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('common:labels.role')}</FormLabel>
                      <FormControl>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <SelectTrigger>
                            <SelectValue placeholder={t('form.placeholders.role')} />
                          </SelectTrigger>
                          <SelectContent>
                            {Object.values(Role).map((role) => (
                              <SelectItem key={role} value={role}>
                                {role.charAt(0) + role.slice(1).toLowerCase()}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                {mode === 'edit' && (
                  <FormField
                    control={form.control}
                    name="status"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('common:labels.status')}</FormLabel>
                        <FormControl>
                          <Select onValueChange={field.onChange} defaultValue={field.value}>
                            <SelectTrigger>
                              <SelectValue placeholder={t('form.placeholders.status')} />
                            </SelectTrigger>
                            <SelectContent>
                              {Object.values(UserStatuses).map((status) => (
                                <SelectItem key={status} value={status}>
                                  {status.charAt(0) + status.slice(1).toLowerCase()}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}
              </div>
            </div>
            <DialogFooter>
              <DialogClose asChild>
                <Button variant="outline">{t('common:actions.cancel')}</Button>
              </DialogClose>
              <Button type="submit" disabled={isPending}>
                {isPending
                  ? mode === 'create'
                    ? t('form.submit.creating')
                    : t('form.submit.updating')
                  : mode === 'create'
                    ? t('form.submit.create')
                    : t('form.submit.update')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
